<script lang="ts">
import {defineComponent} from "vue";
import CircleIcon from "@/assets/icons/checkbox/CircleIcon.vue";
import SquareIcon from "@/assets/icons/checkbox/SquareIcon.vue";

export default defineComponent({
  name: "Checkbox",
  props: {
    title: {
      type: String,
      default: ''
    },

    modelValue: {
      type: Boolean,
      default: false,
    },

    circle: {
      type: Boolean,
      default: false
    },

    checkboxClass: {
      type: String,
      default: ''
    },

    disabled: {
      type: Boolean,
      default: false
    },

    stopPropagation: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    isChecked: {
      get() {
        return this.modelValue;
      },
      set(value: boolean) {
        this.$emit('update:modelValue', value);
      }
    },

    getDataCheckbox() {
      return this.isChecked ? 'checked' : 'default';
    },

    isCircle() {
      return this.circle ? 'checkbox-circle' : '';
    },

    getIcon() {
      return this.circle
          ? CircleIcon
          : SquareIcon;
    }
  },

  methods: {
    changeStatus(event: { stopPropagation: () => void; }) {
      if (this.disabled) return;
      if (this.stopPropagation) event.stopPropagation();

      this.isChecked = !this.isChecked;

      this.$emit('change');
    }
  }
});
</script>

<template>
  <div>
    <div
        class="custom-checkbox d-flex position-relative cursor-pointer"
        :class="[checkboxClass, disabled ? 'disabled' : '']"
        :data-checkbox="getDataCheckbox"
        role="checkbox"
        :aria-checked="isChecked.toString() as 'true' | 'false'"
        :aria-disabled="disabled.toString() as 'true' | 'false'"
        :tabindex="disabled ? -1 : 0"
        @click.stop="changeStatus"
        @keydown.space.prevent="changeStatus"
        @keydown.enter.prevent="changeStatus"
    >
      <div class="custom-checkbox-indicator d-flex flex-row justify-content-center align-items-center"
           :class="isCircle">
        <transition name="fade">
          <component :is="getIcon" v-if="isChecked" class="checkbox-icon"/>
        </transition>
      </div>
      <div class="custom-checkbox-title" v-if="title">{{ title }}</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

[data-checkbox="default"] {
  --checkbox-bg: rgba(11, 20, 24, 1);
  --checkbox-border: rgba(88, 100, 105, 1);
  --checkbox-title: rgba(255, 255, 255, 0.75);
  --checkbox-bg-hover: rgba(35, 69, 84, 0.5);
  --checkbox-border-hover: rgba(184, 232, 255, 0.5);
  --checkbox-title-hover: rgba(184, 232, 255, 1);
}

[data-checkbox="checked"] {
  --checkbox-bg: rgba(35, 69, 84, 1);
  --checkbox-border: rgba(184, 232, 255, 1);
  --checkbox-title: rgba(184, 232, 255, 1);
  --checkbox-bg-hover: rgba(35, 69, 84, 0.5);
  --checkbox-border-hover: rgba(184, 232, 255, 0.5);
  --checkbox-title-hover: rgba(184, 232, 255, 1);
}

.custom-checkbox {
  line-height: normal;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    & .custom-checkbox-indicator {
      border-color: var(--checkbox-border-hover);
      background: var(--checkbox-bg-hover);
    }
  }

  &:active {
    outline: none;
  }

  &.disabled {
    cursor: not-allowed;

    & .custom-checkbox-indicator {
      border-color: rgba(88, 100, 105, 0.5);
      background: rgba(11, 20, 24, 0.5);
    }

    &:hover {
      .custom-checkbox-indicator {
        border-color: rgba(88, 100, 105, 0.5);
        background: rgba(11, 20, 24, 0.5);
      }
    }
  }
}

.custom-checkbox:hover {
  .custom-checkbox-indicator {
    border-color: var(--checkbox-border-hover);
    background: var(--checkbox-bg-hover);
  }

  .custom-checkbox-title {
    color: var(--checkbox-title-hover);
  }
}

.checkbox-circle {
  border-radius: 50% !important;
}

.checkbox-circle .checkbox-icon {
  width: 7px;
  height: 7px;
}

.custom-checkbox-indicator {
  width: 15px;
  height: 15px;

  min-width: 15px;
  min-height: 15px;

  background: var(--checkbox-bg);
  border: 1px solid var(--checkbox-border);
  border-radius: 2px;

  transition: all 0.3s ease;
}

.checkbox-icon {
  display: block;

  width: 9px;
  height: 9px;

  filter: invert(85%) sepia(7%) saturate(1530%) hue-rotate(172deg) brightness(102%) contrast(101%);
}

.custom-checkbox-title {
  font-family: "Artifex CF Regular", serif;
  font-size: 12px;

  padding-left: 5px;
  color: var(--checkbox-title);
}
</style>
